import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiAlertTriangle, FiChevronRight, FiClock, FiDollarSign, FiGrid, FiPlus, FiSettings, FiUsers } from "react-icons/fi";
import { formatNaira } from "../../../utils/dashboardFormat";
import { compactNaira } from "../../../components/MemberProfile/profileModel";
import {
  useGetContributionCategoriesSummaryQuery,
  useGetMembersContributionHistoryQuery,
} from "../../../store/admin/contribution";
import {
  KIND_META, contributorShare, contributorsText, kindTabs, normalizeOverview, overviewFromLegacy, progressText, validKind, whenLabel,
} from "../../../components/Contributions/contribModel";
import { KindChip, Notice, PageHeader, Progress, Skeleton, Tabs, Tile } from "../../../components/Contributions/parts";

const ROUTES = {
  detail: (id) => `/super_admin/contribution/contribution_history/${id}`,
  record: "/super_admin/contribution/record_contribution",
  categories: "/super_admin/contribution/contribution-category",
};

/**
 * Totals by category: what each category has collected, how far along it is, and who is paying.
 * Falls back to the older, plainer list when the server does not have the new totals yet.
 */
export default function CategoryOverview() {
  const [params, setParams] = useSearchParams();
  const kind = validKind(params.get("category_kind"));

  const summary = useGetContributionCategoriesSummaryQuery();
  const summaryMissing = summary.isError && [404, 405].includes(summary.error?.status);
  const legacy = useGetMembersContributionHistoryQuery({ page: 1 }, { skip: !summaryMissing });

  const overview = useMemo(() => {
    if (summary.data) return normalizeOverview(summary.data);
    if (summaryMissing && legacy.data) return overviewFromLegacy(legacy.data);
    return null;
  }, [summary.data, summaryMissing, legacy.data]);

  const loading = summary.isLoading || (summaryMissing && legacy.isLoading);
  const failed = !overview && !loading && (summary.isError || legacy.isError);
  const retry = () => (summaryMissing ? legacy.refetch() : summary.refetch());

  const setKind = (next) => {
    const copy = new URLSearchParams(params);
    if (next === "all") copy.delete("category_kind");
    else copy.set("category_kind", next);
    setParams(copy, { replace: true });
  };

  const categories = overview?.categories || [];
  const shown = kind === "all" ? categories : categories.filter((c) => c.kind === kind);
  const totals = overview?.totals;
  const membersTotal = totals?.membersTotal || 0;

  return (
    <section className="cb" aria-label="Totals by category">
      <PageHeader
        title="Totals by category"
        sub="What each category has collected, and from whom"
        actions={
          <>
            <Link to={ROUTES.categories} className="cb-btn">
              <FiSettings size={16} aria-hidden="true" /> Manage categories
            </Link>
            <Link to={ROUTES.record} className="cb-btn primary">
              <FiPlus size={16} aria-hidden="true" /> Record a payment
            </Link>
          </>
        }
      />

      {overview ? (
        <div className="cb-tiles" role="group" aria-label="Totals">
          <Tile icon={FiDollarSign} tone="green" label="Total collected" value={compactNaira(totals.collected)} />
          {overview.approximate ? null : <Tile icon={FiClock} tone="blue" label="This month" value={compactNaira(totals.thisMonth)} quiet={!totals.thisMonth} />}
          {overview.approximate ? null : (
            <Tile icon={FiUsers} tone="purple" label="Contributors" value={totals.contributors} sub={membersTotal ? `of ${membersTotal}` : ""} quiet={!totals.contributors} />
          )}
          <Tile icon={FiGrid} tone="primary" label="Categories" value={totals.categories} />
        </div>
      ) : null}

      <div className="cb-card">
        {loading ? <Skeleton rows={4} /> : null}

        {failed ? (
          <Notice tone="amber" title="Couldn't load the totals" actionLabel="Try again" onAction={retry}>
            Your categories are fine; we just couldn't fetch the numbers. Try again in a moment.
          </Notice>
        ) : null}

        {overview ? (
          <>
            <Tabs tabs={kindTabs(categories)} active={kind} onChange={setKind} label="Category type" />

            {overview.approximate ? (
              <p className="cb-faint" style={{ marginBottom: 8 }}>
                Showing basic totals. Contributor counts and this-month figures appear once the server is updated.
              </p>
            ) : null}

            {categories.length === 0 ? (
              <Notice title="No categories yet" actionLabel="Create a category" actionTo={ROUTES.categories}>
                Categories are the things members contribute to, like savings, shares or a building fund.
              </Notice>
            ) : shown.length === 0 ? (
              <Notice title={`No ${KIND_META[kind]?.label.toLowerCase() || ""} categories`}>Pick another tab, or add one under Manage categories.</Notice>
            ) : (
              <nav className="cb-cats" aria-label="Categories">
                <div className="cb-listhead" aria-hidden="true">
                  <span>Category</span>
                  <span>Collected</span>
                  <span>Contributors</span>
                  <span>Last payment</span>
                  <span />
                </div>
                {shown.map((c) => {
                  const tone = KIND_META[c.kind].tone;
                  const share = contributorShare(c, membersTotal);
                  return (
                    <Link key={c.id} to={ROUTES.detail(c.id)} className="cb-cat" aria-label={`${c.name}: ${formatNaira(c.collected)} collected. Open details`}>
                      <span className="name">
                        <KindChip kind={c.kind} />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", overflowWrap: "anywhere" }}>{c.name}</span>
                          <span className="cb-sub" style={{ fontWeight: 400 }}>
                            {KIND_META[c.kind].label} &middot; {progressText(c)}
                          </span>
                        </span>
                      </span>
                      <span>
                        <span className="money">{formatNaira(c.collected)}</span>
                        {overview.approximate ? null : <span className="cb-sub">{c.thisMonth ? `${formatNaira(c.thisMonth)} this month` : "Nothing this month"}</span>}
                        {c.progress !== null ? <Progress value={c.progress} tone={tone} label={`${c.progress}% of target`} /> : null}
                      </span>
                      <span>
                        {overview.approximate ? (
                          <span className="cb-faint">—</span>
                        ) : (
                          <>
                            <span className="cb-mute">{contributorsText(c, membersTotal)}</span>
                            {share !== null ? <Progress value={share} tone={tone} label={`${share}% of members`} /> : null}
                          </>
                        )}
                      </span>
                      <span className="cb-mute">{whenLabel(c.lastPaymentAt) || (overview.approximate ? "—" : "No payments yet")}</span>
                      <FiChevronRight className="chev" size={18} aria-hidden="true" />
                    </Link>
                  );
                })}
              </nav>
            )}
          </>
        ) : null}
      </div>

      {overview && !overview.approximate && overview.totals.contributors === 0 && categories.length > 0 ? (
        <div className="cb-banner tone-blue" role="note">
          <FiAlertTriangle size={16} aria-hidden="true" />
          <span>No payments have been recorded in this branch yet. Use Record a payment to add the first one.</span>
        </div>
      ) : null}
    </section>
  );
}
